import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Search, MapPin, CheckCircle2, Globe } from 'lucide-react';
import { Input } from '@/components/ui/input';

const API = '/api';

/**
 * Typeahead search for the Submit page.
 * Groups results into:
 *   1. In Orange Label  — from /api/search/businesses (local)
 *   2. On BTCMap        — from https://api.btcmap.org/v4/places/search
 *   3. On OpenStreetMap — from /api/search/businesses (photon/Nominatim fallback)
 *
 * Props:
 *   - lat, lng (optional) — bias geographically
 *   - onPick(result) — called when a row is clicked
 *   - onManualEntry() — called when user clicks "Can't find my business"
 */
const BusinessSearchAutocomplete = ({ lat, lng, onPick, onManualEntry }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [local, setLocal] = useState([]);
  const [btcmap, setBtcmap] = useState([]);
  const [photon, setPhoton] = useState([]);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) {
      setLocal([]);
      setBtcmap([]);
      setPhoton([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { q: q.trim(), limit: 10 };
        if (lat) params.lat = lat;
        if (lng) params.lng = lng;

        // Run local+photon and BTCMap in parallel
        const [searchRes, btcmapRes] = await Promise.allSettled([
          axios.get(`${API}/search/businesses`, { params }),
          fetch(
            `https://api.btcmap.org/v4/places/search?search=${encodeURIComponent(q.trim())}&limit=20`,
            { signal: AbortSignal.timeout(5000) }
          ).then(r => r.ok ? r.json() : []),
        ]);

        const localResults = searchRes.status === 'fulfilled' ? (searchRes.value.data?.local || []) : [];
        const photonResults = searchRes.status === 'fulfilled' ? (searchRes.value.data?.photon || []) : [];

        // Normalise BTCMap results
        let btcmapResults = [];
        if (btcmapRes.status === 'fulfilled' && Array.isArray(btcmapRes.value)) {
          btcmapResults = btcmapRes.value.slice(0, 8).map(place => {
            const tags = place.tags || {};
            const props = place.osm_json || {};
            const osmTags = props.tags || tags;
            const lat_v = place.lat ?? props.lat ?? null;
            const lng_v = place.lon ?? props.lon ?? null;
            const addrParts = [
              osmTags['addr:housenumber'],
              osmTags['addr:street'],
              osmTags['addr:suburb'],
              osmTags['addr:city'],
              osmTags['addr:state'],
              osmTags['addr:postcode'],
              osmTags['addr:country'],
            ].filter(Boolean);
            return {
              source: 'btcmap',
              id: `btcmap:${place.id || place.osm_id || ''}`,
              btcmap_id: place.id,
              name: osmTags.name || osmTags['name:en'] || place.id || 'Unnamed',
              full_address: addrParts.join(', '),
              addr_housenumber: osmTags['addr:housenumber'] || '',
              addr_street: osmTags['addr:street'] || '',
              addr_suburb: osmTags['addr:suburb'] || '',
              addr_city: osmTags['addr:city'] || '',
              addr_state: osmTags['addr:state'] || '',
              addr_postcode: osmTags['addr:postcode'] || '',
              country_code: (osmTags['addr:country'] || osmTags['addr:country_code'] || '').toUpperCase() || null,
              latitude: lat_v ? parseFloat(lat_v) : null,
              longitude: lng_v ? parseFloat(lng_v) : null,
              website: osmTags.website || osmTags['contact:website'] || '',
              phone: osmTags.phone || osmTags['contact:phone'] || '',
              already_listed: false,
              is_btcmap: true,
            };
          });

          // Deduplicate against local results by name proximity
          const localNames = new Set(localResults.map(r => (r.name || '').toLowerCase().trim()));
          btcmapResults = btcmapResults.filter(b => {
            const bn = (b.name || '').toLowerCase().trim();
            return bn && !localNames.has(bn);
          });
        }

        setLocal(localResults);
        setBtcmap(btcmapResults);
        setPhoton(photonResults);
      } catch (e) {
        setLocal([]);
        setBtcmap([]);
        setPhoton([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, lat, lng]);

  const total = local.length + btcmap.length + photon.length;
  const showNoResults = !loading && q.trim().length >= 2 && total === 0;
  const hasResults = local.length > 0 || btcmap.length > 0 || photon.length > 0;

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Start typing your business name or address..."
          className="h-12 pl-9"
          data-testid="business-search-input"
          autoComplete="off"
        />
      </div>

      {open && (hasResults || showNoResults) && (
        <div className="border border-border rounded-lg overflow-hidden bg-card shadow-sm" data-testid="business-search-results">

          {/* Group 1: In Orange Label */}
          {local.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40">
                In Orange Label ({local.length})
              </div>
              {local.map((r) => (
                <button
                  key={`l-${r.id}`}
                  type="button"
                  onClick={() => { setOpen(false); onPick(r); }}
                  className="w-full text-left px-3 py-2.5 hover:bg-muted/60 transition-colors flex items-start gap-2 border-t border-border first:border-t-0"
                  data-testid={`local-result-${r.id}`}
                >
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{r.name}</div>
                    {r.full_address && (
                      <div className="text-xs text-muted-foreground truncate">{r.full_address}</div>
                    )}
                    <div className="text-[10px] uppercase tracking-wide mt-0.5 text-blue-600 font-semibold">
                      Already listed
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Group 2: On BTCMap */}
          {btcmap.length > 0 && (
            <div>
              <div className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40 ${local.length > 0 ? 'border-t border-border' : ''}`}>
                On BTCMap ({btcmap.length})
              </div>
              {btcmap.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => { setOpen(false); onPick(r); }}
                  className="w-full text-left px-3 py-2.5 hover:bg-muted/60 transition-colors flex items-start gap-2 border-t border-border"
                  data-testid={`btcmap-result-${r.id}`}
                >
                  <Globe className="w-4 h-4 mt-0.5 text-orange-500 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{r.name}</div>
                    {r.full_address && (
                      <div className="text-xs text-muted-foreground truncate">{r.full_address}</div>
                    )}
                    <div className="text-[10px] uppercase tracking-wide mt-0.5 text-orange-500 font-semibold">
                      On BTCMap
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Group 3: On OpenStreetMap */}
          {photon.length > 0 && (
            <div>
              <div className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40 ${(local.length > 0 || btcmap.length > 0) ? 'border-t border-border' : ''}`}>
                On OpenStreetMap ({photon.length})
              </div>
              {photon.map((r) => (
                <button
                  key={`p-${r.id}`}
                  type="button"
                  onClick={() => { setOpen(false); onPick(r); }}
                  className="w-full text-left px-3 py-2.5 hover:bg-muted/60 transition-colors flex items-start gap-2 border-t border-border"
                  data-testid={`photon-result-${r.id}`}
                >
                  <MapPin className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{r.name}</div>
                    {r.full_address && (
                      <div className="text-xs text-muted-foreground truncate">{r.full_address}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {showNoResults && (
            <div className="px-3 py-3 text-sm text-muted-foreground text-center">
              No matches. <button type="button" className="text-primary underline" onClick={onManualEntry}>Enter manually</button>
            </div>
          )}

          <div className="px-3 py-1.5 text-[10px] text-muted-foreground border-t border-border bg-muted/20 flex items-center justify-between">
            <span>{loading ? 'Searching...' : `${total} match${total === 1 ? '' : 'es'}`}</span>
            <button
              type="button"
              onClick={() => { setOpen(false); onManualEntry(); }}
              className="text-primary hover:underline"
              data-testid="manual-entry-link"
            >
              Can't find it? Enter manually →
            </button>
          </div>
        </div>
      )}

      {(!open || q.trim().length < 2) && (
        <p className="text-xs text-muted-foreground">
          Start typing to search Orange Label, BTCMap, and OpenStreetMap. Powered by OpenStreetMap contributors.
        </p>
      )}
    </div>
  );
};

export default BusinessSearchAutocomplete;
